import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Box, Button, Dialog, FormHelperText, FormLabel, Input, Loader, Typography } from '@screentone/core';
import cloneDeep from 'lodash/cloneDeep';

import { WidePageWrapper } from 'components';
import { useAlert } from 'contexts/alert/useAlert';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { ArticleItem, NewsletterDto, NewsletterPageContainer } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { safelyParseContent } from 'utils/temp';
import { NewsletterMultitabSection } from './components/newsletter-multitab-section/NewsletterMultitabSection';
import { TitleHeader } from './components/title-header/TitleHeader';
import { useNewsletterPublish } from './hooks/useNewsletterPublish';

export const NewsletterEdit = () => {
  const {
    root: data,
    metadata,
    renderEntity,
    setNewRoot,
    getSortedEmbargoContent
  } = useDataModelContext<NewsletterPageContainer>();

  const { currentLayout } = usePageTypeSettingsContext();

  const isSubjectDirty = useRef(false);

  const { data: publicationSettingResp } = usePublicationSettings();

  const { handleSendTest, handleSendNow, handleSchedule, validationErrors } = useNewsletterPublish(
    publicationSettingResp?.publicationSetting
  );

  const { currentProperty: routeProperty } = useAuth(); // Original property from URL for navigation

  const { alertError } = useAlert();

  const navigate = useNavigate();

  const [sendNowOpen, setSendNowOpen] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  // Store the original subject template to preserve [headline] placeholder
  const [originalSubjectTemplate] = useState(() => data.attributes.subject);
  const [originalPreHeaderTemplate] = useState(() => data.attributes.preHeaderText);

  const collection = useMemo(() => data.collection[0]?.collection ?? [], [data.collection]);
  const dataAttributes = useMemo(() => data.attributes, [data.attributes]);

  const getDefaultCampaignSubject = useCallback(() => {
    const articleContent: AllessehContent | null =
      data.collection[0].collection.length !== 0
        ? safelyParseContent(data.collection[0].collection[0] as ArticleItem)
        : null;

    const headline: string = articleContent?.data.attributes.headline?.text ?? '';

    // If no articles are present, return the original template with placeholder
    if (!articleContent) {
      return originalSubjectTemplate;
    }

    // If the original template contains the placeholder, replace it with the headline
    if (originalSubjectTemplate.includes('[headline]')) {
      return originalSubjectTemplate.replace('[headline]', headline);
    }

    // If no placeholder is found in the original template, return the original template as is
    return originalSubjectTemplate;
  }, [originalSubjectTemplate, data.collection]);

  const getDefaultCampaignPreHeader = useCallback(() => {
    const articleContent: AllessehContent | null =
      data.collection[0].collection.length !== 0
        ? safelyParseContent(data.collection[0].collection[0] as ArticleItem)
        : null;

    // If no articles are present, return the original template with placeholder
    if (!articleContent) {
      return originalPreHeaderTemplate;
    }

    if (originalPreHeaderTemplate.includes('[dek]')) {
      const dekText =
        articleContent.data.attributes.standfirst?.content?.[0]?.text ??
        articleContent.data.attributes.first_paragraph?.content?.[0]?.text ??
        // content?.[0] is the headline, content[1] is the dek
        articleContent.data.attributes.summary?.content?.[1]?.content?.[0]?.text ??
        '';

      return originalPreHeaderTemplate.replace('[dek]', dekText);
    }

    return originalPreHeaderTemplate;
  }, [originalPreHeaderTemplate, data.collection]);

  useEffect(() => {
    const newRoot = {
      ...data,
      attributes: {
        ...dataAttributes,
        preHeaderText: getDefaultCampaignPreHeader()
      }
    };

    if (typeof isSubjectDirty.current === 'boolean' && !isSubjectDirty.current) {
      newRoot.attributes.subject = getDefaultCampaignSubject();
    }

    setNewRoot(newRoot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  const sendNowClick = async () => {
    try {
      setIsSending(true);
      const publishedNewsletter = await handleSendNow();

      setSendNowOpen(false);

      if (publishedNewsletter) {
        navigate(`/${routeProperty}/newsletters/${publishedNewsletter.metadata.allessehId}`);
      }
      setIsSending(false);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send newsletter';
      alertError(errorMessage);
      setIsSending(false);
    }
  };

  const handleScheduleClick = async (publishUtc: number) => {
    try {
      const scheduledNewsletter = await handleSchedule(publishUtc);

      if (scheduledNewsletter) {
        navigate(
          `/${routeProperty}/newsletters/scheduled/${scheduledNewsletter.allessehId}/${scheduledNewsletter.publishUtc}`
        );
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to schedule newsletter';
      alertError(errorMessage);
    }
  };

  const handleSendTestClick = async (emails: string) => {
    try {
      await handleSendTest(emails);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send test email';
      alertError(errorMessage);
    }
  };

  const updateCustomizableContent = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const newRoot = {
      ...data,
      attributes: {
        ...data.attributes,
        [fieldName]: e.target.value
      }
    };

    setNewRoot(newRoot);

    if (fieldName === 'subject') {
      isSubjectDirty.current = true;
    }
  };

  const latestEmbargoContent = getSortedEmbargoContent('desc').at(0);

  return (
    <WidePageWrapper padding={{ all: 'md' }}>
      <WidePageWrapper.Header>
        <TitleHeader
          readOnly={false}
          newsletterName={metadata.name}
          newsletterId={metadata.allessehId}
          handleSendNowClick={() => setSendNowOpen(true)}
          handleScheduleClick={handleScheduleClick}
          handleSendTestClick={handleSendTestClick}
          publicationSetting={publicationSettingResp?.publicationSetting}
          latestEmbargoContent={latestEmbargoContent}
          preferredTimezone={publicationSettingResp?.publicationSetting.preferredTimezone}
        />

        <Dialog status={sendNowOpen ? 'open' : 'closed'} onDismiss={() => setSendNowOpen(false)}>
          <Dialog.Title id="dialogTitle">Send Campaign</Dialog.Title>
          <Dialog.Content id="dialogDesc">
            <Typography variant="header4" data-testid="newsletter-send-now-msg">
              {data.attributes.subject}
            </Typography>
            <Typography>Are you sure you want to send this immediately?</Typography>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              secondary
              onClick={() => setSendNowOpen(false)}
              data-testid="newsletter-cancel-send"
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button primary onClick={sendNowClick} data-testid="newsletter-send" disabled={isSending}>
              {isSending ? (
                <>
                  <Loader size="md" margin={{ right: 'xs' }} />
                  Sending...
                </>
              ) : (
                'Send'
              )}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </WidePageWrapper.Header>
      <WidePageWrapper.Left>
        {renderEntity(cloneDeep(data), {
          hierarchyId: '',
          extraProps: { module: { layout: currentLayout } },
          withoutDragNDrop: false
        })}
        <Box margin={{ top: 'md', bottom: 'md' }}>
          <Box.Title data-testid="newsletter-edit-custom-content">Custom Content</Box.Title>
          <Box.Content>
            <FormLabel labelPosition="top" label="Sender Name" margin={{ bottom: 'md' }} fullWidth>
              <Input
                data-testid="newsletter-edit-sender-name"
                value={data.attributes.senderName}
                onChange={(evt) => updateCustomizableContent(evt, 'senderName')}
              />
            </FormLabel>
            <FormLabel labelPosition="top" label="Reply-To Email" margin={{ bottom: 'md' }} fullWidth>
              <Input
                data-testid="newsletter-edit-reply-email"
                value={data.attributes.replyToEmail}
                onChange={(evt) => updateCustomizableContent(evt, 'replyToEmail')}
              />
            </FormLabel>
            <FormLabel required labelPosition="top" label="Subject" margin={{ bottom: 'md' }} fullWidth>
              <Input
                data-testid="newsletter-edit-subject-line"
                value={data.attributes.subject}
                onChange={(evt) => updateCustomizableContent(evt, 'subject')}
              />
              {validationErrors.subject && (
                <FormHelperText error data-testid="newsletter-edit-subject-error">
                  {validationErrors.subject}
                </FormHelperText>
              )}
            </FormLabel>
            <FormLabel required labelPosition="top" label="Pre-Header Text" margin={{ bottom: 'md' }} fullWidth>
              <Input
                data-testid="newsletter-edit-header-text"
                value={data.attributes.preHeaderText}
                onChange={(evt) => updateCustomizableContent(evt, 'preHeaderText')}
              />
              {validationErrors.preHeaderText && (
                <FormHelperText error data-testid="newsletter-edit-header-text-error">
                  {validationErrors.preHeaderText}
                </FormHelperText>
              )}
            </FormLabel>
          </Box.Content>
        </Box>
      </WidePageWrapper.Left>
      <WidePageWrapper.Right>
        <NewsletterMultitabSection
          newsletterName={metadata.name}
          campaign={{ root: data, metadata } as NewsletterDto}
          publicationSetting={publicationSettingResp?.publicationSetting}
        />
      </WidePageWrapper.Right>
    </WidePageWrapper>
  );
};
